import { siteConfig } from "@/config/site";
import { landingContent } from "@/content/landing";
import { pricingPlans, scriptarioPricingPlans } from "@/lib/plans";
import {
  formatPaddleAmount,
  formatPaddleMonthlyEquivalent,
  getCurrencyFormattingLocale,
  getPaddleFormattedListAmount,
  getPaddleListAmount,
  type PaddlePriceBreakdown,
} from "@/lib/paddle-pricing";

const RUB = decodeURIComponent("%E2%82%BD");
const RUB_PER_MINUTE = `${RUB}/мин`;

export type ScriptarioCurrency = "USD" | "EUR" | "BRL";

const scriptarioLocalizedPrices: Record<string, Record<string, { price: string; yearlyPrice: string; currency: ScriptarioCurrency }>> = {
  en: {
    free: { price: "$0", yearlyPrice: "$0", currency: "USD" },
    basic: { price: "$5,90", yearlyPrice: "$35,40", currency: "USD" },
    standard: { price: "$9,90", yearlyPrice: "$59,40", currency: "USD" },
    expert: { price: "$19,90", yearlyPrice: "$119,40", currency: "USD" },
  },
  es: {
    free: { price: "€0", yearlyPrice: "€0", currency: "EUR" },
    basic: { price: "€7,99", yearlyPrice: "€47,94", currency: "EUR" },
    standard: { price: "€12,99", yearlyPrice: "€77,94", currency: "EUR" },
    expert: { price: "€39,99", yearlyPrice: "€239,94", currency: "EUR" },
  },
  "pt-br": {
    free: { price: "$0", yearlyPrice: "$0", currency: "USD" },
    basic: { price: "$5,90", yearlyPrice: "$35,40", currency: "USD" },
    standard: { price: "$9,90", yearlyPrice: "$59,40", currency: "USD" },
    expert: { price: "$19,90", yearlyPrice: "$119,40", currency: "USD" },
  },
  it: {
    free: { price: "€0", yearlyPrice: "€0", currency: "EUR" },
    basic: { price: "€7,99", yearlyPrice: "€47,94", currency: "EUR" },
    standard: { price: "€12,99", yearlyPrice: "€77,94", currency: "EUR" },
    expert: { price: "€39,99", yearlyPrice: "€239,94", currency: "EUR" },
  },
};

/**
 * Ставка за минуту сверх лимита задаётся валютой, а не локалью.
 *
 * Раньше она была готовой строкой в таблице выше, и после того как витрина
 * перестала выводить страну из локали, `/es/` показывал цену плана в долларах
 * от Paddle и перерасход в евро прямо под ней. Берём валюту из того же превью,
 * что и цену.
 */
const SCRIPTARIO_OVERAGE_RATE_BY_CURRENCY: Record<ScriptarioCurrency, Record<string, number>> = {
  USD: { basic: 0.02, standard: 0.02, expert: 0.01 },
  EUR: { basic: 0.02, standard: 0.02, expert: 0.01 },
  BRL: { basic: 0.06, standard: 0.06, expert: 0.02 },
};

/**
 * Строка от Paddle всегда важнее локальной таблицы.
 *
 * Таблица знает три валюты, и раньше любая четвёртая молча становилась USD:
 * мексиканец видел цену плана в песо и ставку за минуту в долларах — в его
 * глазах перерасход дешевле реального в двадцать раз. Поэтому если превью дало
 * цену минуты, берём её как есть, а без неё показываем ставку только для валют,
 * которые таблица действительно знает.
 */
function buildScriptarioOverage(
  planId: string,
  currencyCode: string | null | undefined,
  locale: string,
  formattedUnitPrice?: string | null,
) {
  const paddleAmount = String(formattedUnitPrice || "").trim();
  if (paddleAmount) return paddleAmount;

  const currency = String(currencyCode || "").trim().toUpperCase();
  const rate = SCRIPTARIO_OVERAGE_RATE_BY_CURRENCY[currency as ScriptarioCurrency]?.[planId];
  if (rate == null) return "";

  return new Intl.NumberFormat(
    getCurrencyFormattingLocale(currency, locale === "pt-br" ? "pt-BR" : locale),
    { style: "currency", currency },
  ).format(rate);
}

function parsePrice(s: string): number {
  const raw = s.replace(/[^\d,.-]/g, "");
  if (!raw) return 0;
  const hasCommaDecimals = /,\d{1,2}$/.test(raw);
  const normalized = hasCommaDecimals
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw.replace(/,/g, "");
  return Number(normalized) || 0;
}

function fmt(n: number, currency?: ScriptarioCurrency): string {
  if (siteConfig.siteId === "scriptario") {
    if (currency === "EUR") {
      return `€${n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (currency === "BRL") {
      return `R$${n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    // Евро и реал уже печатались с двумя знаками, а доллар — нет, и когда
    // Paddle не отвечал, витрина показывала «$5.9» вместо «$5.90».
    return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  return `${n.toLocaleString("ru-RU")} ${RUB}`;
}

export interface PlanPriceViewInput {
  planId: string;
  /** Цена и зачёркнутая цена считаются для выбранного периода. */
  isYear?: boolean;
  /** Превью Paddle для выбранного периода (Scriptario). */
  priceBreakdown?: PaddlePriceBreakdown | null;
  /** Превью Paddle для месячного плана — источник зачёркнутой цены в годовом режиме. */
  monthlyBreakdown?: PaddlePriceBreakdown | null;
  extraMinuteBreakdown?: PaddlePriceBreakdown | null;
}

export interface PlanPriceView {
  isFree: boolean;
  /** Цена за месяц для выбранного периода. */
  price: string;
  /** Зачёркнутая месячная цена — только в годовом режиме. */
  oldPrice?: string;
  /** Полная сумма годового платежа — только в годовом режиме. */
  yearTotal?: string;
  /** «500 минут в месяц» из данных плана. */
  minutes: string;
  /** «сверх лимита — 2 ₽/мин» или пустая строка, если ставки нет. */
  overage: string;
}

/**
 * Единственный источник цен для витрин тарифов.
 *
 * Логика раньше жила внутри `PricingCard`, и мобильный пейвол из одной
 * карточки не мог её переиспользовать: ему нужны цены обоих периодов сразу.
 * Вынесено без изменения поведения — обе витрины считают одно и то же.
 */
export function buildPlanPriceView({
  planId,
  isYear,
  priceBreakdown,
  monthlyBreakdown,
  extraMinuteBreakdown,
}: PlanPriceViewInput): PlanPriceView {
  const pricing = landingContent.pricing;
  const basePlanData = (siteConfig.siteId === "scriptario" ? scriptarioPricingPlans : pricingPlans).find((p) => p.id === planId);
  const localizedPrice = siteConfig.siteId === "scriptario"
    ? scriptarioLocalizedPrices[siteConfig.locale]?.[planId] || scriptarioLocalizedPrices.en[planId]
    : null;
  const planData = basePlanData && localizedPrice
    ? { ...basePlanData, price: localizedPrice.price, yearlyPrice: localizedPrice.yearlyPrice }
    : basePlanData;
  const scriptarioCurrency = localizedPrice?.currency;
  const monthlyRaw = planData ? parsePrice(planData.price) : 0;
  const yearlyRaw = planData?.yearlyPrice ? parsePrice(planData.yearlyPrice) : 0;
  const isFree = monthlyRaw === 0;

  let price: string;
  let oldPrice: string | undefined;
  let yearTotal: string | undefined;

  if (isFree) {
    price = siteConfig.siteId === "scriptario" ? "0" : `0 ${RUB}`;
  } else if (siteConfig.siteId === "scriptario" && priceBreakdown && getPaddleFormattedListAmount(priceBreakdown)) {
    const pageLocale = siteConfig.locale === "pt-br" ? "pt-BR" : siteConfig.locale;
    // Строка Paddle остаётся фоллбэком на случай, если сырой суммы в ответе нет.
    const subtotal = formatPaddleAmount(getPaddleListAmount(priceBreakdown), priceBreakdown.currencyCode, pageLocale)
      || getPaddleFormattedListAmount(priceBreakdown)
      || "";
    if (isYear) {
      price = formatPaddleMonthlyEquivalent(
        getPaddleListAmount(priceBreakdown),
        priceBreakdown.currencyCode,
        pageLocale,
      ) || subtotal;
      oldPrice = formatPaddleAmount(
        getPaddleListAmount(monthlyBreakdown),
        monthlyBreakdown?.currencyCode || priceBreakdown.currencyCode,
        pageLocale,
      ) || getPaddleFormattedListAmount(monthlyBreakdown) || fmt(monthlyRaw, scriptarioCurrency);
      yearTotal = subtotal;
    } else {
      price = subtotal;
    }
  } else if (isYear) {
    const effectiveMonthly = yearlyRaw > 0 ? yearlyRaw / 12 : 0;
    price = fmt(effectiveMonthly, scriptarioCurrency);
    oldPrice = fmt(monthlyRaw, scriptarioCurrency);
    yearTotal = fmt(yearlyRaw, scriptarioCurrency);
  } else {
    price = fmt(monthlyRaw, scriptarioCurrency);
  }

  const overageRaw = planData?.costPerMinute || "";
  let overage = "";
  if (siteConfig.siteId === "scriptario" && !isFree) {
    const overageAmount = buildScriptarioOverage(
      planId,
      priceBreakdown?.currencyCode || scriptarioCurrency,
      siteConfig.locale,
      formatPaddleAmount(
        getPaddleListAmount(extraMinuteBreakdown),
        extraMinuteBreakdown?.currencyCode,
        siteConfig.locale === "pt-br" ? "pt-BR" : siteConfig.locale,
      ) || getPaddleFormattedListAmount(extraMinuteBreakdown),
    );
    if (overageAmount) {
      overage = `${pricing.overagePrefix}${overageAmount}${pricing.overageSuffix}`;
    }
  } else if (overageRaw && overageRaw !== "Недоступно" && overageRaw !== "Unavailable") {
    const num = parseFloat(overageRaw.replace(/\s/g, "").replace(RUB_PER_MINUTE, "").replace("$/min", "").replace(",", "."));
    if (!isNaN(num)) {
      const overageNum = Number.isInteger(num) ? num.toString() : num.toFixed(2).replace(".", siteConfig.siteId === "scriptario" ? "." : ",");
      overage = `${pricing.overagePrefix}${overageNum}${pricing.overageSuffix}`;
    }
  }

  const minutes = planData?.transcriptionMinutes
    ? pricing.minutesPerMonth(planData.transcriptionMinutes)
    : "";

  return { isFree, price, oldPrice, yearTotal, minutes, overage };
}
